import { useTranslations } from '@vocab/react';
import { Stack, Text } from 'braid-design-system';
import React, {
  forwardRef,
  type Ref,
  type RefObject,
  useContext,
  useRef,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { SECTION_JOB_AD_REFERENCE } from 'src/app/shared/constants/pageSectionName';
import {
  useFeatureToggleData,
  FEATURE_LINKOUT,
} from 'src/app/shared/featureToggle';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { formatDateFromIsoDate } from 'src/app/utils/date';
import AdExpiryDate from 'src/app/v1/views/components/AdExpiryDate';
import { ApplicationUrl } from 'src/app/v1/views/components/ApplicationUrl';
import { JobAdReference } from 'src/app/v1/views/components/JobAdReference';
import { ManageCandidateApplicationsSectionAlert } from 'src/app/v1/views/components/ManageCandidateApplicationsSectionAlert';
import { NotProvidedText } from 'src/app/v1/views/components/NotProvidedText';
import { RoleRequirements } from 'src/app/v1/views/components/RoleRequirements';
import { EditFlowDataContext } from 'src/app/v1/views/context/cajaFlowData';
import { useCanUpdateExpiryDate } from 'src/app/v1/views/hooks/useCanUpdateExpiryDate';
import { EditablePageSection } from 'src/app/v1/views/sections/EditablePageSection';
import { ContentLoader } from 'src/app/v1/views/shared/ContentLoader';
import type { AppState } from 'src/types/appState';
import type {
  ApplicationUrlRef,
  JobAdReferenceRef,
} from 'src/types/formFields';
import { ApplyViaOptions } from 'src/types/linkOut';
import type { SectionProps, SectionRef } from 'src/types/sections';

import translations from './.vocab';

interface ReadProps {
  refs: {
    rrWidgetRef: RefObject<any>;
  };
}

export const Read = ({ refs }: ReadProps) => {
  const {
    applicationFormUrl,
    expiryDate,
    jobAdReference,
    linkOutApplyVia,
    questionnaireUrl,
  } = useSelector(
    (state: AppState) => ({
      applicationFormUrl: state.formData.applicationFormUrl,
      expiryDate: state.formData.expiryDate,
      jobAdReference: state.formData.jobAdReference,
      linkOutApplyVia: state.formData.linkOutApplyVia,
      questionnaireUrl: state.formData.questionnaireUrl,
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const { loading } = useContext(EditFlowDataContext);
  const { t } = useTranslations(translations);
  const hasQuestionnaireUrl = Boolean(questionnaireUrl);
  const hasLinkOutEnabled = useFeatureToggleData(FEATURE_LINKOUT);
  const canUpdateExpiryDate = useCanUpdateExpiryDate();
  const { getLocale } = useConfig();

  return (
    <Stack space="large">
      {!loading && (hasLinkOutEnabled || hasQuestionnaireUrl) && (
        <ManageCandidateApplicationsSectionAlert
          hasLinkOutEnabled={hasLinkOutEnabled}
          hasQuestionnaireUrl={hasQuestionnaireUrl}
        />
      )}
      {hasLinkOutEnabled && (
        <Stack space="medium">
          <Text weight="strong">
            {t(
              'manage-candidate-applications-section-read-write-linkout-heading',
            )}
          </Text>
          <ContentLoader loading={loading} lines={['75%']}>
            <Stack space="medium">
              {linkOutApplyVia === ApplyViaOptions.EXTERNAL_WEBSITE && (
                <>
                  <Text>
                    {t(
                      'manage-candidate-applications-section-read-write-linkout-external-heading',
                    )}
                  </Text>
                  <Text>{applicationFormUrl}</Text>
                </>
              )}
              {linkOutApplyVia === ApplyViaOptions.SEEK_WEBSITE && (
                <Text>
                  {t(
                    'manage-candidate-applications-section-read-linkout-seek-heading',
                  )}
                </Text>
              )}
            </Stack>
          </ContentLoader>
        </Stack>
      )}

      <Stack space="medium">
        <Text weight="strong">
          {t(
            'manage-candidate-applications-section-read-job-reference-heading',
          )}
        </Text>
        <ContentLoader loading={loading} lines={['50%']}>
          {jobAdReference ? <Text>{jobAdReference}</Text> : <NotProvidedText />}
        </ContentLoader>
      </Stack>

      {canUpdateExpiryDate && (
        <Stack space="medium">
          <Text weight="strong">
            {t(
              'manage-candidate-applications-section-read-write-expiry-date-heading',
            )}
          </Text>
          <ContentLoader loading={loading} lines={['25%']}>
            <Text>
              {expiryDate && formatDateFromIsoDate(expiryDate, getLocale())}
            </Text>
          </ContentLoader>
        </Stack>
      )}

      {Boolean(questionnaireUrl) && (
        <Stack space="medium">
          <Text weight="strong">
            {t('manage-candidate-applications-section-read-questions-heading')}
          </Text>
          <ContentLoader loading={loading} lines={['75%']} height={200}>
            <RoleRequirements rrWidgetRef={refs.rrWidgetRef} />
          </ContentLoader>
        </Stack>
      )}
    </Stack>
  );
};

interface EditProps {
  refs: {
    applicationUrlRef: RefObject<ApplicationUrlRef>;
    jobAdReferenceRef: RefObject<JobAdReferenceRef>;
  };
}

export const Edit = ({ refs }: EditProps) => {
  const { linkOutApplyVia } = useSelector(
    (state: AppState) => ({
      linkOutApplyVia: state.formData.linkOutApplyVia,
    }),
    shallowEqual,
  );
  const { t } = useTranslations(translations);
  const hasLinkOutEnabled = useFeatureToggleData(FEATURE_LINKOUT);
  const canUpdateExpiryDate = useCanUpdateExpiryDate();

  return (
    <Stack space="medium">
      {hasLinkOutEnabled &&
        linkOutApplyVia === ApplyViaOptions.EXTERNAL_WEBSITE && (
          <Stack space="large">
            <Stack space="medium">
              <Text weight="strong">
                {t(
                  'manage-candidate-applications-section-read-write-linkout-heading',
                )}
              </Text>
              <Text>
                {t(
                  'manage-candidate-applications-section-read-write-linkout-external-heading',
                )}
              </Text>
            </Stack>

            <Stack space="small">
              <ApplicationUrl ref={refs.applicationUrlRef} />
              <JobAdReference ref={refs.jobAdReferenceRef} isRequired />
            </Stack>
          </Stack>
        )}

      {linkOutApplyVia === ApplyViaOptions.SEEK_WEBSITE && (
        <JobAdReference ref={refs.jobAdReferenceRef} />
      )}

      {canUpdateExpiryDate && (
        <Stack space="small">
          <Text weight="strong">
            {t(
              'manage-candidate-applications-section-read-write-expiry-date-heading',
            )}
          </Text>
          <AdExpiryDate />
        </Stack>
      )}
    </Stack>
  );
};

export const ManageCandidateApplicationsSection = forwardRef(
  (_props: SectionProps, ref: Ref<SectionRef>) => {
    const applicationUrlRef = useRef<ApplicationUrlRef>(null);
    const jobAdReferenceRef = useRef<JobAdReferenceRef>(null);
    const rrWidgetRef = useRef<any>();

    const { t } = useTranslations(translations);

    return (
      <EditablePageSection
        title={t('manage-candidate-applications-section-heading')}
        name={SECTION_JOB_AD_REFERENCE}
        ref={ref}
        isOptional={true}
        fields={[applicationUrlRef, jobAdReferenceRef]}
        read={<Read refs={{ rrWidgetRef }} />}
        edit={<Edit refs={{ applicationUrlRef, jobAdReferenceRef }} />}
      />
    );
  },
);
